/**
 * Add AI model price (TMMPDM-0011). Remounts when opened. TMMPDM-0088 (TMMAPI-0152): the input and
 * output rates must be above 0, as the server requires. TMMPDM-0099: Now omits `effectiveFrom`; a
 * date sends midnight UTC. Change price (Step 1b) posts a new row for the server's in-force row.
 * TMMPDM-0103: the fields sit in three rows of two columns. TMMPDM-0104: a price is added from a
 * catalogue model's row, so the model is fixed (read-only in the top-left cell) and Currency is
 * the editable select beside it. A server 400 `fields.model` lands on the Model cell.
 */
import { useState, type ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import type { AiModelPriceCreateRequest, AiModelPriceRow } from '@/api/tmm/types';
import { formatMicroPerMtok } from '@/format';
import { currencyToMicro, microCaption, microToInput } from './microPrice';

const CURRENCIES = ['USD', 'CRC', 'MXN', 'EUR', 'GBP', 'CAD'] as const;

/** The fields this form shows; a server refusal that names none of them is the form's own line. */
const FIELDS = [
  'model',
  'currency',
  'effectiveFrom',
  'inputPerMtokMicro',
  'outputPerMtokMicro',
  'cacheReadPerMtokMicro',
  'cacheWritePerMtokMicro',
  'note',
] as const;

const NOTE_MAX = 500;

const WHEN_LABEL_ID = 'add-price-when';

export interface PriceChangeTarget {
  model: string;
  currency: string;
  inputPerMtokMicro: number;
  outputPerMtokMicro: number;
  cacheReadPerMtokMicro: number;
  cacheWritePerMtokMicro: number;
}

interface AddPriceDialogProps {
  open: boolean;
  /** Add mode: the catalogue model the price is for (fixed). Ignored in change mode. */
  model?: string;
  /** The in-force row being replaced. Absent in add mode. */
  change?: PriceChangeTarget | null;
  onClose: () => void;
  onAdded: (row: AiModelPriceRow) => void;
}

function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function realUtcDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

function invalidMoney(value: string): boolean {
  if (value.trim() === '') return true;
  const n = Number(value);
  return !Number.isFinite(n) || n < 0;
}

/** Strictly more than half, either way. A zero baseline moves when the new amount is not zero. */
function movedMoreThanHalf(oldMicro: number, newMicro: number): boolean {
  if (oldMicro === 0) return newMicro !== 0;
  return Math.abs(newMicro - oldMicro) / Math.abs(oldMicro) > 0.5;
}

export function AddPriceDialog({
  open,
  model = '',
  change = null,
  onClose,
  onAdded,
}: AddPriceDialogProps) {
  const formKey = change
    ? `change-${change.model}-${change.currency}-${change.inputPerMtokMicro}-${change.outputPerMtokMicro}`
    : `add-${model}`;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {open ? (
        <AddPriceFields
          key={formKey}
          model={model}
          change={change}
          onClose={onClose}
          onAdded={onAdded}
        />
      ) : null}
    </Dialog>
  );
}

/** Six fields, three rows of two equal columns; one column below `sm`. DOM order is visual order. */
const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
  gap: 2,
} as const;

function ReadOnlyField({
  label,
  value,
  error,
}: {
  label: string;
  value: string;
  /** A server refusal naming this field (TMMPDM-0104). */
  error?: string | undefined;
}) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
      {error ? (
        <Typography role="alert" variant="caption" color="error">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
}

function AddPriceFields({
  model,
  change,
  onClose,
  onAdded,
}: Omit<AddPriceDialogProps, 'open' | 'model'> & {
  model: string;
  change: PriceChangeTarget | null;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [currency, setCurrency] = useState('USD');
  const [when, setWhen] = useState<'now' | 'date'>('now');
  const [date, setDate] = useState('');
  const [input, setInput] = useState(() =>
    change ? microToInput(change.inputPerMtokMicro) : '0.00',
  );
  const [output, setOutput] = useState(() =>
    change ? microToInput(change.outputPerMtokMicro) : '0.00',
  );
  const [cacheRead, setCacheRead] = useState(() =>
    change ? microToInput(change.cacheReadPerMtokMicro) : '0.00',
  );
  const [cacheWrite, setCacheWrite] = useState(() =>
    change ? microToInput(change.cacheWritePerMtokMicro) : '0.00',
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const sentModel = change ? change.model : model;
  const sentCurrency = change ? change.currency : currency;

  const clearWarnings = () => setWarnings([]);

  const rateChange = (set: (value: string) => void) => (event: ChangeEvent<HTMLInputElement>) => {
    set(event.target.value);
    clearWarnings();
  };

  const softLines = (): string[] => {
    const lines: string[] = [];
    const inputN = Number(input);
    if (Number(cacheRead) >= inputN) {
      lines.push(t('ai.form.warn.cacheRead', 'Cache read is usually cheaper than input.'));
    }
    if (Number(cacheWrite) <= inputN) {
      lines.push(t('ai.form.warn.cacheWrite', 'Cache write usually costs more than input.'));
    }
    if (change) {
      const rates = [
        {
          label: t('ai.form.input', 'Input / Mtok'),
          oldMicro: change.inputPerMtokMicro,
          value: input,
        },
        {
          label: t('ai.form.output', 'Output / Mtok'),
          oldMicro: change.outputPerMtokMicro,
          value: output,
        },
        {
          label: t('ai.form.cacheRead', 'Cache read / Mtok'),
          oldMicro: change.cacheReadPerMtokMicro,
          value: cacheRead,
        },
        {
          label: t('ai.form.cacheWrite', 'Cache write / Mtok'),
          oldMicro: change.cacheWritePerMtokMicro,
          value: cacheWrite,
        },
      ];
      for (const rate of rates) {
        const newMicro = currencyToMicro(rate.value);
        if (!movedMoreThanHalf(rate.oldMicro, newMicro)) continue;
        lines.push(
          t('ai.form.warn.moved', '{{rate}} changes from {{from}} to {{to}}.', {
            rate: rate.label,
            from: formatMicroPerMtok(rate.oldMicro, sentCurrency, locale),
            to: formatMicroPerMtok(newMicro, sentCurrency, locale),
          }),
        );
      }
    }
    return lines;
  };

  const handleSubmit = async () => {
    const next: Record<string, string> = {};
    const moreThanZero = t('ai.form.moreThanZero', 'Must be more than zero.');
    if (invalidMoney(input)) next.inputPerMtokMicro = t('ai.form.required', 'Required.');
    else if (currencyToMicro(input) <= 0) next.inputPerMtokMicro = moreThanZero;
    if (invalidMoney(output)) next.outputPerMtokMicro = t('ai.form.required', 'Required.');
    else if (currencyToMicro(output) <= 0) next.outputPerMtokMicro = moreThanZero;
    if (
      next.inputPerMtokMicro === undefined &&
      next.outputPerMtokMicro === undefined &&
      Number(output) <= Number(input)
    ) {
      next.outputPerMtokMicro = t('ai.form.outputAboveInput', 'Output must cost more than input.');
    }
    if (invalidMoney(cacheRead)) next.cacheReadPerMtokMicro = t('ai.form.required', 'Required.');
    if (invalidMoney(cacheWrite)) next.cacheWritePerMtokMicro = t('ai.form.required', 'Required.');
    if (note.trim().length > NOTE_MAX) {
      next.note = t('datedSettings.form.tooLong', 'Use at most {{max, number}} characters.', {
        max: NOTE_MAX,
      });
    }
    if (when === 'date') {
      if (!realUtcDate(date)) {
        next.effectiveFrom = t('ai.form.dateRequired', 'Choose a date.');
      } else if (date <= utcToday()) {
        next.effectiveFrom = t('ai.form.dateAfterToday', 'Choose a date after today, or pick Now.');
      }
    }
    setFieldErrors(next);
    setFormError(null);
    if (Object.keys(next).length > 0) return;

    if (warnings.length === 0) {
      const lines = softLines();
      if (lines.length > 0) {
        setWarnings(lines);
        return;
      }
    }

    const body: AiModelPriceCreateRequest = {
      model: sentModel,
      currency: sentCurrency,
      inputPerMtokMicro: currencyToMicro(input),
      outputPerMtokMicro: currencyToMicro(output),
      cacheReadPerMtokMicro: currencyToMicro(cacheRead),
      cacheWritePerMtokMicro: currencyToMicro(cacheWrite),
    };
    if (when === 'date') body.effectiveFrom = `${date}T00:00:00Z`;
    if (note.trim() !== '') body.note = note.trim();
    setSubmitting(true);
    try {
      const row = await adminApi.createAiModelPrice(body);
      onAdded(row);
    } catch (err: unknown) {
      if (err instanceof ApiError && isValidationFailed(err)) {
        const marked = validationFields(err, t, {
          model: t(
            'ai.form.modelNotInCatalogue',
            "This model isn't in the catalogue. Add the model on the Models tab first.",
          ),
          effectiveFrom: t(
            'datedSettings.errors.future',
            'Must be in the future. Pick Now to apply it straight away.',
          ),
          outputPerMtokMicro: t('ai.form.outputAboveInput', 'Output must cost more than input.'),
        });
        if (FIELDS.some((key) => marked[key] !== undefined)) {
          setFieldErrors(marked);
        } else {
          setFormError(t('ai.form.error', "Couldn't add the price."));
        }
      } else {
        setFormError(errorCopy(err, t, t('ai.form.error', "Couldn't add the price.")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const caption = (value: string) =>
    t('ai.form.microCaption', 'Sends {{micro}} micro-units / Mtok', { micro: microCaption(value) });

  const submitLabel =
    warnings.length > 0
      ? t('ai.form.saveAnyway', 'Save anyway')
      : change
        ? t('ai.form.changeSubmit', 'Change price')
        : t('ai.form.submit', 'Add price');

  return (
    <>
      <DialogTitle>
        {change
          ? t('ai.form.changeTitle', 'Change price: {{name}} ({{currency}})', {
              name: change.model,
              currency: change.currency,
            })
          : t('ai.form.title', 'Add price: {{name}}', { name: model })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          {change ? (
            <Typography variant="body2">
              {t(
                'ai.form.changeExplain',
                "Prices aren't edited in place. This starts a new price; AI usage before it keeps the price it was charged at.",
              )}
            </Typography>
          ) : (
            <Typography variant="body2">
              {t('ai.form.addExplain', 'Set the price in one currency.')}
            </Typography>
          )}
          <Box data-testid="price-fields" sx={GRID_SX}>
            <ReadOnlyField
              label={t('ai.form.model', 'Model')}
              value={sentModel}
              error={fieldErrors.model}
            />
            {change ? (
              <ReadOnlyField label={t('ai.form.currency', 'Currency')} value={change.currency} />
            ) : (
              <TextField
                select
                label={t('ai.form.currency', 'Currency')}
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
                error={Boolean(fieldErrors.currency)}
                helperText={fieldErrors.currency}
                fullWidth
              >
                {CURRENCIES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label={t('ai.form.input', 'Input / Mtok')}
              value={input}
              onChange={rateChange(setInput)}
              error={Boolean(fieldErrors.inputPerMtokMicro)}
              helperText={fieldErrors.inputPerMtokMicro || caption(input)}
              required
              fullWidth
            />
            <TextField
              label={t('ai.form.output', 'Output / Mtok')}
              value={output}
              onChange={rateChange(setOutput)}
              error={Boolean(fieldErrors.outputPerMtokMicro)}
              helperText={fieldErrors.outputPerMtokMicro || caption(output)}
              required
              fullWidth
            />
            <TextField
              label={t('ai.form.cacheRead', 'Cache read / Mtok')}
              value={cacheRead}
              onChange={rateChange(setCacheRead)}
              error={Boolean(fieldErrors.cacheReadPerMtokMicro)}
              helperText={fieldErrors.cacheReadPerMtokMicro || caption(cacheRead)}
              required
              fullWidth
            />
            <TextField
              label={t('ai.form.cacheWrite', 'Cache write / Mtok')}
              value={cacheWrite}
              onChange={rateChange(setCacheWrite)}
              error={Boolean(fieldErrors.cacheWritePerMtokMicro)}
              helperText={fieldErrors.cacheWritePerMtokMicro || caption(cacheWrite)}
              required
              fullWidth
            />
          </Box>
          <FormControl error={Boolean(fieldErrors.effectiveFrom)}>
            <FormLabel id={WHEN_LABEL_ID}>{t('datedSettings.form.when', 'When')}</FormLabel>
            <RadioGroup
              aria-labelledby={WHEN_LABEL_ID}
              value={when}
              onChange={(event) => {
                setWhen(event.target.value === 'date' ? 'date' : 'now');
                setFieldErrors(({ effectiveFrom: _dropped, ...others }) => others);
              }}
            >
              <FormControlLabel
                value="now"
                control={<Radio />}
                label={t('datedSettings.form.now', 'Now')}
              />
              <FormControlLabel
                value="date"
                control={<Radio />}
                label={t('ai.form.fromDate', 'From a date')}
              />
            </RadioGroup>
          </FormControl>
          {when === 'date' ? (
            <TextField
              type="date"
              label={t('datedSettings.form.date', 'Date (UTC)')}
              value={date}
              onChange={(event) => {
                setDate(event.target.value);
                setFieldErrors(({ effectiveFrom: _dropped, ...others }) => others);
              }}
              error={Boolean(fieldErrors.effectiveFrom)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          ) : null}
          {fieldErrors.effectiveFrom ? (
            <Typography role="alert" variant="body2" color="error">
              {fieldErrors.effectiveFrom}
            </Typography>
          ) : null}
          <TextField
            label={t('datedSettings.form.note', 'Note (optional)')}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setFieldErrors(({ note: _dropped, ...others }) => others);
            }}
            error={Boolean(fieldErrors.note)}
            helperText={fieldErrors.note}
            multiline
            minRows={2}
            fullWidth
            slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
          />
          {warnings.length > 0 ? (
            <Alert severity="warning">
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {warnings.map((line) => (
                  <Typography key={line} component="li" variant="body2">
                    {line}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ai.form.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void handleSubmit()} busy={submitting}>
          {submitLabel}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
